import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SectionHeading from './SectionHeading';
import { useInView } from './motion';
import { useCardTilt } from './useCardTilt';
import { useCapability } from './three/useCapability';
import { startCardMagicShow, trackInteraction } from './cardMagicReveal';
import { cardImage } from '../../lib/cardAssets';
import { useT, useLang } from '../../i18n/LanguageContext';

// A representative spread across the five groups (A–E).
const cardIds = ['A2', 'B5', 'C1', 'D3', 'E6', 'A8', 'C7', 'B2', 'D6', 'E2'];

export default function CardShowcase() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { ref, visible } = useInView();
  const t = useT();
  const { lang } = useLang();
  const imgs = cardIds.map((id) => cardImage(id, lang));
  const scrollRef = useRef(null);
  const overlayRef = useRef(null);
  const sparkleRef = useRef(null);
  const hasStartedRef = useRef(false);
  const capability = useCapability();

  const scrollByCards = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' });
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks -- imgs.length is a fixed constant (10), never changes across renders
  const tiltRefs = imgs.map(() => useCardTilt({ maxAngle: 6, enabled: capability.renderCursor }));

  // The deck's infinite "magic" show — see cardMagicReveal.js. Starts once
  // when the strip first scrolls into view, then loops forever: a single
  // stage card morphs through all 10 faces, expands into the real strip
  // (which takes over, fully interactive), idles, collapses, vanishes, and
  // repeats. `hasStartedRef` guards against restarting on a later `visible`
  // flip (useInView's `once` default already prevents that, but this stays
  // correct even if that default changes) — the loop, once started, keeps
  // running regardless of later scroll position.
  useEffect(() => {
    if (!visible || hasStartedRef.current) return;
    hasStartedRef.current = true;

    const stripEl = scrollRef.current;
    const overlayEl = overlayRef.current;
    const sparkleEl = sparkleRef.current;
    if (!stripEl || !overlayEl || !sparkleEl) return undefined;

    const firstCard = stripEl.children[0];
    const secondCard = stripEl.children[1];
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 210;
    const cardGap = firstCard && secondCard
      ? secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().right
      : 20;

    const activityTracker = trackInteraction([stripEl]);
    const stopShow = startCardMagicShow({
      overlayEl,
      sparkleLayerEl: sparkleEl,
      images: imgs,
      cardWidth,
      cardGap,
      realStripEl: stripEl,
      activityTracker,
    });

    return () => {
      stopShow();
      activityTracker.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `imgs` is derived from `lang` at render time but this effect is intentionally guarded by hasStartedRef to run only once (see comment above); it must not re-fire on a later lang change.
  }, [visible]);

  // Inset must stay inside the scroll strip's own px padding (below) so the
  // arrow sits in the buffer zone next to the cards, not on top of the edge
  // card's artwork — was 4/12 (32px/96px), bigger than the strip's 6/7 (48px/56px)
  // padding, so the button overlapped the last visible card.
  const arrowSx = (side) => ({
    position: 'absolute', [side]: { xs: 2, md: 3 }, top: '50%', transform: 'translateY(-50%)', zIndex: 4,
    bgcolor: 'rgba(251,248,244,0.92)', '&:hover': { bgcolor: '#fff' }, boxShadow: 3,
    width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 },
  });

  return (
    <Box sx={{ bgcolor: theme.home.ink, py: theme.home.sectionPy, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: theme.home.grain, opacity: 0.6, pointerEvents: 'none' }} />
      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 }, position: 'relative' }}>
        <SectionHeading
          eyebrow={t('home.cardShowcase.eyebrow')}
          accent={theme.home.coral}
          title={<Box component="span" sx={{ color: theme.home.paper }}>{t('home.cardShowcase.title')}</Box>}
          lede={<Box component="span" sx={{ color: 'rgba(251,248,244,0.7)' }}>{t('home.cardShowcase.lede')}</Box>}
        />
      </Box>

      {/* Arrow-controlled strip on every size; scrollbar hidden, touch-swipe still works */}
      <Box ref={ref} sx={{ position: 'relative', mt: { xs: 5, md: 7 } }}>
        <IconButton onClick={() => scrollByCards(-1)} aria-label={t('home.cardShowcase.arrowPrev')} sx={arrowSx('left')}>
          <ChevronLeftIcon />
        </IconButton>

        <Box sx={{ position: 'relative' }}>
          <Box
            ref={scrollRef}
            sx={{
              display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 2.5 }, px: { xs: 6, md: 7 }, overflowX: 'auto', pb: 1,
              scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {imgs.map((src, i) => {
              const tiltRef = tiltRefs[i];
              return (
                <Box
                  key={i}
                  sx={{ position: 'relative', flex: '0 0 auto', width: { xs: 150, md: 210 } }}
                >
                  <Box
                    ref={tiltRef}
                    data-cursor="plus"
                    sx={{
                      position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden',
                      border: '4px solid rgba(251,248,244,0.92)', boxShadow: '0 20px 40px -18px rgba(0,0,0,0.6)',
                      scrollSnapAlign: 'center', transition: 'transform 0.15s ease-out',
                    }}
                  >
                    <Box component="img" src={src} alt="" sx={{ display: 'block', width: '100%' }} />
                    {/* Sheen overlay on top of the <img>, not as its own
                        background — an <img>'s bitmap paints over its own CSS
                        background, so a gradient set there is never visible. */}
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: `radial-gradient(circle at var(--tilt-sheen-x, 50%) var(--tilt-sheen-y, 50%), rgba(255,255,255,0.3), transparent 60%)`,
                        backgroundBlendMode: 'overlay',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Magic-show overlay: cardMagicReveal.js's infinite state
              machine renders its own <img> clones here (morph/expand/
              collapse/vanish). Opacity toggles between this and the real
              strip above — never both interactive/visible at once. */}
          <Box ref={overlayRef} className="card-magic-overlay" aria-hidden="true" sx={{ position: 'absolute', inset: 0, zIndex: 6, pointerEvents: 'none' }} />
          {/* Always-on sparkle layer, independent of the overlay's opacity
              so idle-phase glints still render above the live, interactive
              real strip. */}
          <Box ref={sparkleRef} aria-hidden="true" sx={{ position: 'absolute', inset: 0, zIndex: 7, pointerEvents: 'none' }} />
        </Box>

        <IconButton onClick={() => scrollByCards(1)} aria-label={t('home.cardShowcase.arrowNext')} sx={arrowSx('right')}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 }, mt: 4, textAlign: { xs: 'center', md: 'left' } }}>
        <Typography sx={{ color: 'rgba(251,248,244,0.6)', fontSize: '0.85rem', mb: 2, display: { xs: 'block', md: 'none' } }}>
          {t('home.cardShowcase.mobileNotice')}
        </Typography>
        <Button
          variant="contained" size="large" onClick={() => navigate('/pricing')}
          sx={{ px: 3.5, py: 1.4, borderRadius: 999, bgcolor: theme.home.coralText, '&:hover': { bgcolor: theme.home.coralHover } }}
        >
          {t('home.cardShowcase.ctaExplore')}
        </Button>
      </Box>
    </Box>
  );
}
